const formatLocationKeyAsLevelLabel = locationKey => {
  const normalizedKey = String(locationKey ?? '')
    .trim()
    .replace(/^level-/, '')

  return normalizedKey
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const extractSectionNumberFromLocationKey = locationKey => {
  const key = String(locationKey ?? '').trim()
  if (!key) {
    return null
  }

  const sectionSuffixMatch = key.match(/(?:^|-)section-(\d+)$/)
  if (sectionSuffixMatch) {
    return sectionSuffixMatch[1]
  }

  const segments = key.split('-').filter(Boolean)
  const lastSegment = segments[segments.length - 1]
  return /^\d+$/.test(lastSegment) ? lastSegment : null
}

const findLocationKeyMatch = (locationKey, locationLevels = []) => {
  const key = String(locationKey ?? '').trim()
  if (!key) {
    return null
  }

  for (const level of locationLevels) {
    if (level.key === key) {
      return { type: 'level', label: level.label }
    }

    for (const section of level.sections || []) {
      if (section.key === key) {
        return {
          type: 'section',
          label: String(section.label ?? '').trim()
        }
      }
    }
  }

  return null
}

const formatSectionLabelPart = label => {
  const trimmed = String(label ?? '').trim()
  if (!trimmed) {
    return ''
  }

  if (/^section\s+/i.test(trimmed)) {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
  }

  if (/^\d+$/.test(trimmed)) {
    return `Section ${trimmed}`
  }

  return trimmed
}

const compressSectionLabels = labels => {
  const formattedLabels = (Array.isArray(labels) ? labels : [])
    .map(label => formatSectionLabelPart(label))
    .filter(Boolean)

  if (formattedLabels.length <= 1) {
    return formattedLabels[0] || ''
  }

  let prefix = formattedLabels[0]
  for (const label of formattedLabels.slice(1)) {
    let index = 0
    while (
      index < prefix.length &&
      index < label.length &&
      prefix[index] === label[index]
    ) {
      index += 1
    }
    prefix = prefix.slice(0, index)
  }

  const lastSpace = prefix.lastIndexOf(' ')
  if (lastSpace > 0) {
    prefix = prefix.slice(0, lastSpace + 1)
  }

  const suffixes = formattedLabels
    .map(label => (label.startsWith(prefix) ? label.slice(prefix.length).trim() : ''))
    .filter(Boolean)

  if (suffixes.length === formattedLabels.length && prefix.trim().length > 0) {
    return `${prefix.trimEnd()} ${suffixes.join(', ')}`.replace(/\s+/g, ' ').trim()
  }

  return formattedLabels.join(', ')
}

const parseLocationKeyForSectionLabel = (locationKey, locationLevels = []) => {
  const match = findLocationKeyMatch(locationKey, locationLevels)
  if (match) {
    return match
  }

  const sectionNumber = extractSectionNumberFromLocationKey(locationKey)
  if (sectionNumber) {
    return { type: 'section', label: sectionNumber }
  }

  return {
    type: 'level',
    label: formatLocationKeyAsLevelLabel(locationKey)
  }
}

export const buildVendorSectionLabel = (
  storedSectionLabel,
  locationKeys = [],
  locationLevels = []
) => {
  const storedLabel = String(storedSectionLabel ?? '').trim()
  const keys = (Array.isArray(locationKeys) ? locationKeys : [])
    .map(key => String(key ?? '').trim())
    .filter(Boolean)

  if (!keys.length) {
    return storedLabel
  }

  const parsed = keys.map(key => parseLocationKeyForSectionLabel(key, locationLevels))
  const sectionLabels = parsed
    .filter(item => item.type === 'section')
    .map(item => item.label)
    .filter(Boolean)
  const levelLabels = parsed
    .filter(item => item.type === 'level')
    .map(item => item.label)
    .filter(Boolean)

  if (sectionLabels.length) {
    const sectionPart = compressSectionLabels(sectionLabels)
    if (!levelLabels.length) {
      return sectionPart
    }
    return [sectionPart, ...levelLabels].join(', ')
  }

  if (storedLabel) {
    return storedLabel
  }

  if (levelLabels.length) {
    return levelLabels.join(', ')
  }

  return ''
}

export const parseVendorLocationFilters = (locationKeys = [], locationLevels = []) => {
  const keys = Array.isArray(locationKeys) ? locationKeys : []
  const levelKeySet = new Set()
  const sectionKeySet = new Set()

  keys.forEach(key => {
    const normalizedKey = String(key ?? '').trim()
    if (!normalizedKey) {
      return
    }

    const matchingLevel = locationLevels.find(level => level.key === normalizedKey)
    if (matchingLevel) {
      levelKeySet.add(normalizedKey)
      return
    }

    const parentLevel = locationLevels.find(level =>
      (level.sections || []).some(section => section.key === normalizedKey)
    )

    if (parentLevel) {
      levelKeySet.add(parentLevel.key)
      sectionKeySet.add(normalizedKey)
    }
  })

  return {
    levelKeys: [...levelKeySet],
    sectionKeys: [...sectionKeySet]
  }
}

export const getSectionOptionsForSelectedLevels = (locationLevels = [], selectedLevelKeys = []) =>
  (Array.isArray(selectedLevelKeys) ? selectedLevelKeys : []).flatMap(levelKey => {
    const level = locationLevels.find(item => item.key === levelKey)
    if (!level) {
      return []
    }

    return (level.sections || []).map(section => ({
      key: section.key,
      label: section.label,
      levelKey: level.key
    }))
  })

export const buildVendorLocationFilterKeys = (
  selectedLevelKeys = [],
  selectedSectionKeys = [],
  locationLevels = []
) => {
  const result = []

  ;(Array.isArray(selectedLevelKeys) ? selectedLevelKeys : []).forEach(levelKey => {
    const level = locationLevels.find(item => item.key === levelKey)
    if (!level) {
      return
    }

    const sections = level.sections || []
    if (!sections.length) {
      result.push(levelKey)
      return
    }

    const selectedForLevel = (Array.isArray(selectedSectionKeys) ? selectedSectionKeys : [])
      .filter(sectionKey => sections.some(section => section.key === sectionKey))

    result.push(...selectedForLevel)
  })

  return [...new Set(result)]
}
